import config from 'config'
import editor from 'shared/editor/utils'
import { selectIsInNoteMode } from 'selectors/location'
import { doOpenNotePage, doOpenTicketPage } from 'actions/pages'

import { selectCurrentTicketId } from 'ducks/tickets/selectors/selectCurrentTicketId'
import { selectHelplyAccountId } from 'ducks/accounts/selectors/selectHelplyAccountId'
import { selectDraftByTicketId } from 'ducks/drafts2/selectors'
import { handleDraftChange } from 'ducks/drafts2/operations'
import { doUpdateEditorVisibility } from 'ducks/editor/operations'

/**
 * Insert HTML produced by the Helply copilot into the current ticket's composer,
 * as a public reply or an internal note. Opens the composer (Groove's can be
 * collapsed) and inserts into the active editor at the cursor (like Zendesk's
 * `ticket.editor.insert`). For a note it switches to the note page first, since
 * Groove's note is a separate route rather than a comment-type toggle.
 */
export const doInsertHelplyCopilotText = ({ html, asNote = false }) => async (
  dispatch,
  getState
) => {
  const ticketId = selectCurrentTicketId(getState())
  if (!ticketId || !html) return

  const draftType = asNote ? 'note' : 'reply'

  // Ensure a draft of this type exists before writing into it.
  await handleDraftChange(dispatch, draftType, undefined, ticketId, null, {
    touched: true,
  })

  const draft = selectDraftByTicketId(getState(), ticketId, draftType)

  if (asNote) {
    // "Insert as note": Groove's note is a separate page (not a comment-type
    // toggle like Zendesk), so make the note composer the active one and put the
    // content in. Open the composer in case it's collapsed.
    dispatch(doUpdateEditorVisibility(true))

    if (selectIsInNoteMode(getState())) {
      // Note composer already active — insert at the cursor and sync the draft.
      editor.insertContent(html, { focus: true })
      const content = editor.getContent()
      handleDraftChange(
        dispatch,
        draftType,
        draft && draft.id,
        ticketId,
        null,
        {
          touched: true,
          body: typeof content === 'string' && content ? content : html,
        }
      )
    } else {
      // Not in note mode: write the note draft, then switch to the note page —
      // the note composer mounts showing the draft body.
      const existing = (draft && draft.body) || ''
      handleDraftChange(
        dispatch,
        draftType,
        draft && draft.id,
        ticketId,
        null,
        {
          touched: true,
          body: existing ? `${existing}${html}` : html,
        }
      )
      dispatch(doOpenNotePage(ticketId))
    }
    return
  }

  // Reply: open the composer (Groove's can be collapsed) and make the reply
  // composer the active one. Groove's note is a separate page, so if the agent
  // is on the note page we must switch back to the reply page first — otherwise
  // insertContent would write into the visible note editor while we save a
  // reply draft, leaving the two out of sync.
  dispatch(doUpdateEditorVisibility(true))

  if (!selectIsInNoteMode(getState())) {
    // Reply composer already active — insert at the cursor and sync the draft
    // (handleDraftChange updates redux only and won't re-render the editor).
    editor.insertContent(html, { focus: true })
    const content = editor.getContent()
    handleDraftChange(dispatch, draftType, draft && draft.id, ticketId, null, {
      touched: true,
      body: typeof content === 'string' && content ? content : html,
    })
  } else {
    // On the note page: write the reply draft, then switch to the reply page —
    // the reply composer mounts showing the draft body.
    const existing = (draft && draft.body) || ''
    handleDraftChange(dispatch, draftType, draft && draft.id, ticketId, null, {
      touched: true,
      body: existing ? `${existing}${html}` : html,
    })
    dispatch(doOpenTicketPage(ticketId))
  }
}

/**
 * Replace the active composer (reply or note, whichever is open) with HTML from
 * the Helply copilot — the Format quick action. Mirrors Zendesk's
 * `client.set('ticket.comment.text', html)`: writes the draft body AND pushes
 * the content into the visible TinyMCE composer imperatively, since
 * `handleDraftChange` updates redux only and the editor guards against
 * re-syncing once it holds content/focus.
 */
export const doSetHelplyCopilotEditor = ({ html }) => async (
  dispatch,
  getState
) => {
  const state = getState()
  const ticketId = selectCurrentTicketId(state)
  if (!ticketId || typeof html !== 'string') return

  // Target whichever composer is active (route-driven note vs reply).
  const draftType = selectIsInNoteMode(state) ? 'note' : 'reply'

  await handleDraftChange(dispatch, draftType, undefined, ticketId, null, {
    touched: true,
  })

  const draft = selectDraftByTicketId(getState(), ticketId, draftType)

  handleDraftChange(dispatch, draftType, draft && draft.id, ticketId, null, {
    touched: true,
    body: html,
  })

  // Groove's composer can be collapsed (unlike Zendesk's); make sure it's open
  // so the result is visible. If it was closed, it mounts from the draft body
  // we just set.
  dispatch(doUpdateEditorVisibility(true))

  // Reflect the replace in the visible composer (same mechanism canned-reply
  // undo uses).
  editor.setContent(html, { focus: true })

  // setContent leaves the caret at the start; move it to the end.
  const tinyEditor = editor.getEditor()
  if (tinyEditor && tinyEditor.selection) {
    tinyEditor.selection.select(tinyEditor.getBody(), true)
    tinyEditor.selection.collapse(false)
  }
}

/**
 * Fetch a short-lived, account-scoped grant that the copilot iframe exchanges
 * with Helply for a session. In production this will be minted by Groove's
 * backend (signed with the shared Groove<->Helply secret) and only issued when
 * the account's Helply Copilot addon is active. Resolves to the grant, or null.
 *
 * TEMP(dev): the Groove backend endpoint isn't built yet, so we mint the grant
 * from Helply's stand-in (`/api/copilot/groove/dev-grant`), keyed by the Helply
 * account id (see `selectHelplyAccountId`). That route is enabled locally and on
 * Helply's Vercel preview/staging, and 404s only in Helply production — so this
 * works on staging but not production. Swap it for the Groove endpoint —
 * expected contract is a JSON body `{ grant: string }` — once it ships.
 */
export const doFetchHelplyCopilotGrant = () => async (dispatch, getState) => {
  try {
    const accountId = selectHelplyAccountId(getState())
    if (!accountId) return null
    const base = (config.helplyCopilotUrl || '').replace(/\/$/, '')
    const response = await fetch(`${base}/api/copilot/groove/dev-grant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountId }),
    })
    if (!response.ok) return null
    const data = await response.json()
    return (data && data.grant) || null
  } catch (e) {
    return null
  }
}
